import 'slick-carousel';

type PanelsModule = {
  elements: {
    roots: HTMLElement[];
  };
  init: () => void;
  bindForRoot: (root: HTMLElement) => void;
  setupPanelToggles: (root: HTMLElement) => void;
  setupDepartmentHover: (root: HTMLElement) => void;
  initPanelSlider: (panel: HTMLElement) => void;
  getPanelConfig: (panel: HTMLElement) => {
    slidesToShowDesktop: number;
    slidesToScrollDesktop: number;
    slidesToShowMobile: number;
    slidesToScrollMobile: number;
    centerPadding: string;
    centerMode: boolean;
    autoplay: boolean;
    autoplaySpeed: number;
    infinite: boolean;
  };
  equalizeHeights: ($slider: JQuery<HTMLElement>) => void;
  updateNavigation: (panel: HTMLElement, currentSlide: number) => void;
  handleResize: () => void;
};

const panels: PanelsModule = {
  elements: {
    roots: Array.from(document.querySelectorAll<HTMLElement>('.module--panels')),
  },

  init() {
    if (this.elements.roots.length === 0) return;
    this.elements.roots.forEach(root => this.bindForRoot(root));
    window.addEventListener('resize', this.handleResize.bind(this));
  },

  bindForRoot(root: HTMLElement) {
    this.setupPanelToggles(root);
    this.setupDepartmentHover(root);

    const facultyPanel = root.querySelector<HTMLElement>('.module__panel[data-panel="faculty"]');
    const alumniPanel = root.querySelector<HTMLElement>('.module__panel[data-panel="alumni"]');
    if (facultyPanel) this.initPanelSlider(facultyPanel);
    if (alumniPanel) this.initPanelSlider(alumniPanel);
  },

  setupPanelToggles(root: HTMLElement) {
    const triggers = Array.from(root.querySelectorAll<HTMLButtonElement>('.module__panel--trigger'));

    triggers.forEach(trigger => {
      trigger.addEventListener('click', () => {
        const panel = trigger.closest('.module__panel') as HTMLElement | null;
        if (!panel) return;
        const content = panel.querySelector<HTMLElement>('.module__panel--content');
        if (!content) return;

        const isOpen = content.classList.contains('open');

        // Toggle only the current panel; do not force-close others
        content.classList.toggle('open', !isOpen);
        trigger.classList.toggle('active', !isOpen);

        // If we just opened this panel, ensure its slider positions correctly
        if (!isOpen) {
          const slider = panel.querySelector<HTMLElement>('.module__slider');
          if (slider) {
            const $slick = window.jQuery(slider);
            if ($slick.hasClass('slick-initialized')) {
              $slick.slick('setPosition');
            }
          }
        }
      });
    });
  },

  setupDepartmentHover(root: HTMLElement) {
    const panel = root.querySelector<HTMLElement>('.module__panel[data-panel="departments"]');
    if (!panel) return;
    const list = panel.querySelector<HTMLElement>('.module__departments');
    if (!list) return;
    const overlay = list.querySelector<HTMLElement>('.module__panel--overlay');

    list.addEventListener('mouseover', (e) => {
      const li = (e.target as HTMLElement).closest('li');
      if (!li || !panel.contains(li) || li.classList.contains('module__panel--overlay')) return;
      const url = li.getAttribute('data-image') || '';
      if (url) {
        list.setAttribute('style', `background-image:url(${url});`);
        overlay?.setAttribute('style', 'opacity: 0.5;');
      }
    });

    list.addEventListener('mouseout', () => {
      list.removeAttribute('style');
      overlay?.setAttribute('style', 'opacity: 0;');
    });
  },

  getPanelConfig(panel: HTMLElement) {
    const ds = panel.dataset;
    const slidesToShowDesktop = Number(ds.slidesDesktop || ds.pagesizeDesktop || 2) || 2;
    const slidesToScrollDesktop = Number(ds.scrollDesktop || ds.pagesizeDesktop || slidesToShowDesktop) || slidesToShowDesktop;
    const slidesToShowMobile = Number(ds.slidesMobile || 1) || 1;
    const slidesToScrollMobile = Number(ds.scrollMobile || 1) || 1;
    const centerPadding = (ds.centerPadding as string) || '';
    const centerMode = ds.centerMode ? ds.centerMode === 'true' : true;
    const autoplay = ds.autoplay ? ds.autoplay === 'true' : false;
    const autoplaySpeed = Number(ds.autoplaySpeed || 6000) || 6000;
    const infinite = ds.infinite ? ds.infinite === 'true' : true;

    return {
      slidesToShowDesktop,
      slidesToScrollDesktop,
      slidesToShowMobile,
      slidesToScrollMobile,
      centerPadding,
      centerMode,
      autoplay,
      autoplaySpeed,
      infinite,
    };
  },

  initPanelSlider(panel: HTMLElement) {
    const slider = panel.querySelector<HTMLElement>('.module__slider');
    // Desktop nav is in sidebar, mobile nav is in mobile footer
    const desktopNav = panel.querySelector<HTMLElement>('.module__panel--sidebar .module__footer .module__slider--nav.desktop');
    const mobileNav = panel.querySelector<HTMLElement>('.module__footer--mobile .module__slider--nav.mobile');
    if (!slider || !desktopNav || !mobileNav) return;

    const cfg = this.getPanelConfig(panel);
    const $slick = window.jQuery(slider);

    $slick.on('init', (_event: JQuery.Event, slick: JQuerySlick) => {
      const totalSlides = slick.slideCount || 0;
      const desktopPageCount = Math.ceil(totalSlides / cfg.slidesToScrollDesktop);

      desktopNav.innerHTML = '';
      mobileNav.innerHTML = '';

      for (let i = 0; i < desktopPageCount; i++) {
        desktopNav.insertAdjacentHTML('beforeend', `<button type="button" class="slide-link" aria-label="Go to page ${i + 1}"><svg width="30" height="24" viewBox="0 0 30 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path id="Vector" d="M14.9276 0.670298C6.97533 2.70607 0.5 9.55889 0.5 15.8268C0.5 22.0947 6.97533 25.4437 14.9276 23.4021C22.8741 21.2173 29.5 14.3645 29.5 8.1023C29.5 1.84015 22.8741 -1.52031 14.9276 0.670298Z" fill="white" stroke="white" stroke-width="3"></path></svg></button>`);
      }

      for (let i = 0; i < totalSlides; i++) {
        mobileNav.insertAdjacentHTML('beforeend', `<button type="button" class="slide-link" aria-label="Go to slide ${i + 1}"><svg width="30" height="24" viewBox="0 0 30 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path id="Vector" d="M14.9276 0.670298C6.97533 2.70607 0.5 9.55889 0.5 15.8268C0.5 22.0947 6.97533 25.4437 14.9276 23.4021C22.8741 21.2173 29.5 14.3645 29.5 8.1023C29.5 1.84015 22.8741 -1.52031 14.9276 0.670298Z" fill="white" stroke="white" stroke-width="3"></path></svg></button>`);
      }

      this.updateNavigation(panel, 0);
      setTimeout(() => this.equalizeHeights($slick), 100);
    });

    $slick.slick({
      slidesToShow: cfg.slidesToShowDesktop,
      slidesToScroll: cfg.slidesToScrollDesktop,
      adaptiveHeight: false,
      slide: '.module__panel--slide',
      rows: 0,
      arrows: false,
      dots: false,
      autoplay: cfg.autoplay,
      autoplaySpeed: cfg.autoplaySpeed,
      infinite: cfg.infinite,
      responsive: [
        {
          breakpoint: 999,
          settings: {
            slidesToShow: cfg.slidesToShowMobile,
            slidesToScroll: cfg.slidesToScrollMobile,
            centerPadding: cfg.centerPadding,
            centerMode: cfg.centerMode,
            autoplay: cfg.autoplay,
          }
        }
      ]
    });

    $slick.on('beforeChange', (_e: JQuery.Event, _slick: JQuerySlick, _currentSlide: number, nextSlide: number) => {
      this.updateNavigation(panel, nextSlide);
    });

    window.jQuery(panel).on('click', '.module__slider--nav .slide-link', function(this: HTMLElement) {
      const isMobile = window.innerWidth < 999;
      // Desktop nav is in sidebar, mobile nav is in mobile footer
      const navSet = isMobile ?
        panel.querySelector('.module__footer--mobile .module__slider--nav.mobile') :
        panel.querySelector('.module__panel--sidebar .module__footer .module__slider--nav.desktop');
      if (!navSet) return;
      const dotIndex = Array.from(navSet.children).indexOf(this);
      const slideIndex = isMobile ? dotIndex : dotIndex * cfg.slidesToScrollDesktop;
      if ($slick.hasClass('slick-initialized')) {
        $slick.slick('slickGoTo', slideIndex);
      }
    });

    $slick.on('afterChange', () => this.equalizeHeights($slick));
  },

  handleResize() {
    this.elements.roots.forEach(root => {
      const panels = Array.from(root.querySelectorAll<HTMLElement>('.module__panel'));
      panels.forEach(panel => {
        const slider = panel.querySelector<HTMLElement>('.module__slider');
        if (!slider) return;
        const $slick = window.jQuery(slider);
        if ($slick.hasClass('slick-initialized')) {
          $slick.slick('setPosition');
          this.equalizeHeights($slick);
          this.updateNavigation(panel, $slick.slick('slickCurrentSlide'));
        }
      });
    });
  },

  equalizeHeights($slider: JQuery<HTMLElement>) {
    if (!$slider.hasClass('slick-initialized')) return;

    $slider.find('.module__panel--slide').height('auto');

    let maxHeight = 0;
    $slider.find('.module__panel--slide').each(function() {
      maxHeight = Math.max(maxHeight, window.jQuery(this).outerHeight());
    });

    const slideHeight = maxHeight + 2;
    $slider.find('.module__panel--slide').height(slideHeight);
    if ($slider.hasClass('slick-initialized')) {
      $slider.slick('setPosition');
    }
  },

  updateNavigation(panel: HTMLElement, currentSlide: number) {
    // Desktop nav is in sidebar, mobile nav is in mobile footer
    const desktopNav = panel.querySelector<HTMLElement>('.module__panel--sidebar .module__footer .module__slider--nav.desktop');
    const mobileNav = panel.querySelector<HTMLElement>('.module__footer--mobile .module__slider--nav.mobile');
    if (!desktopNav || !mobileNav) return;

    const isMobile = window.innerWidth < 999;

    if (isMobile) {
      mobileNav.querySelectorAll('.slide-link').forEach((link, index) => {
        link.classList.toggle('active', index === currentSlide);
      });
    } else {
      desktopNav.querySelectorAll('.slide-link').forEach((link, index) => {
        link.classList.toggle('active', index === Math.floor(currentSlide / this.getPanelConfig(panel).slidesToScrollDesktop));
      });
    }
  }
};

export default panels;
